import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Plus, Users, GameController, Envelope, UsersThree, Trash, Check, X
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GroupsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [invites, setInvites] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', description: '', game_type: '' });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [groupsRes, invitesRes] = await Promise.all([
        axios.get(`${API_URL}/api/groups`, { withCredentials: true }),
        axios.get(`${API_URL}/api/groups/invites/mine`, { withCredentials: true }),
      ]);
      setGroups(groupsRes.data);
      setInvites(invitesRes.data);
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createGroup = async (e) => {
    e.preventDefault();
    if (!newGroup.name.trim()) return toast.error('Name required');
    try {
      await axios.post(`${API_URL}/api/groups`, newGroup, { withCredentials: true });
      toast.success('Group created!');
      setShowCreate(false);
      setNewGroup({ name: '', description: '', game_type: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const acceptInvite = async (groupId) => {
    try {
      await axios.post(`${API_URL}/api/groups/${groupId}/join`, {}, { withCredentials: true });
      toast.success('Joined group!');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const declineInvite = async (inviteId) => {
    // Just remove from UI — backend will keep status as pending but user won't see it
    setInvites(prev => prev.filter(i => i.id !== inviteId));
    toast.success('Invite dismissed');
  };

  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?';

  return (
    <div className="app-container" data-testid="groups-page">
      <header className="p-6 pb-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-white" data-testid="back-button">
            <ArrowLeft size={24} weight="bold" />
          </button>
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Crews</h1>
        </div>
        <button onClick={() => setShowCreate(!showCreate)}
          className="w-10 h-10 rounded-full bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] flex items-center justify-center" data-testid="create-group-button">
          <Plus size={20} weight="bold" className="text-white" />
        </button>
      </header>

      <div className="px-6 pb-32 space-y-4 mt-2">
        {/* Create Group Form */}
        <AnimatePresence>
          {showCreate && (
            <motion.form onSubmit={createGroup} initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
              className="p-4 rounded-2xl bg-[#181824] border border-[#9D4CDD]/30 space-y-3" data-testid="create-group-form">
              <input type="text" placeholder="Crew name (e.g. Friday Night Catan)" value={newGroup.name} onChange={(e) => setNewGroup(p => ({ ...p, name: e.target.value }))}
                className="input-field text-sm" data-testid="group-name-input" />
              <input type="text" placeholder="Description (optional)" value={newGroup.description} onChange={(e) => setNewGroup(p => ({ ...p, description: e.target.value }))}
                className="input-field text-sm" />
              <input type="text" placeholder="Game type (optional)" value={newGroup.game_type} onChange={(e) => setNewGroup(p => ({ ...p, game_type: e.target.value }))}
                className="input-field text-sm" />
              <button type="submit" className="btn-primary w-full text-sm" data-testid="submit-create-group">Create Crew</button>
            </motion.form>
          )}
        </AnimatePresence>

        {/* Pending Invites */}
        {invites.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-[#A1A1AA] mb-2 uppercase tracking-wider">Invites</p>
            {invites.map((inv, i) => (
              <motion.div key={inv.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="p-4 rounded-2xl bg-[#181824] border border-[#FACC15]/20 mb-2 flex items-center gap-3" data-testid={`group-invite-${i}`}>
                <Envelope size={24} weight="duotone" className="text-[#FACC15] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm truncate">{inv.group_name}</p>
                  <p className="text-[#71717A] text-[10px]">Invited by {inv.invited_by_name}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => acceptInvite(inv.group_id)} className="w-8 h-8 rounded-full bg-[#39FF14]/20 flex items-center justify-center" data-testid={`accept-invite-${i}`}>
                    <Check size={14} weight="bold" className="text-[#39FF14]" />
                  </button>
                  <button onClick={() => declineInvite(inv.id)} className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center" data-testid={`decline-invite-${i}`}>
                    <X size={14} weight="bold" className="text-red-400" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Groups List */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2].map(i => <div key={i} className="h-24 rounded-2xl bg-[#181824] animate-pulse" />)}
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-16 text-[#71717A]">
            <UsersThree size={56} weight="duotone" className="mx-auto mb-4 text-[#3F3F46]" />
            <p className="text-base font-medium mb-1">No crews yet</p>
            <p className="text-xs">Create a crew and invite friends to play!</p>
          </div>
        ) : (
          groups.map((g, i) => (
            <motion.div key={g.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              onClick={() => navigate(`/groups/${g.id}`)}
              className="p-4 rounded-2xl bg-[#181824] border border-white/5 hover:border-[#9D4CDD]/30 transition-all cursor-pointer" data-testid={`group-card-${i}`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#9D4CDD]/30 to-[#7C3AED]/30 flex items-center justify-center">
                  <UsersThree size={24} weight="duotone" className="text-[#9D4CDD]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-sm truncate">{g.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[#71717A] text-xs flex items-center gap-1">
                      <Users size={12} /> {g.member_count} member{g.member_count !== 1 ? 's' : ''}
                    </span>
                    {g.game_type && (
                      <span className="text-[#00F0FF] text-[10px] px-2 py-0.5 rounded-md bg-[#00F0FF]/10">{g.game_type}</span>
                    )}
                  </div>
                </div>
              </div>
              {g.description && (
                <p className="text-[#71717A] text-xs mt-2 line-clamp-1">{g.description}</p>
              )}
              {/* Member avatars */}
              <div className="flex -space-x-2 mt-3">
                {(g.members || []).slice(0, 5).map((m, mi) => (
                  m.avatar ? (
                    <img key={mi} src={m.avatar} alt="" className="w-7 h-7 rounded-full border-2 border-[#181824] object-cover" />
                  ) : (
                    <div key={mi} className="w-7 h-7 rounded-full border-2 border-[#181824] bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] flex items-center justify-center text-white text-[8px] font-bold">{getInitials(m.name)}</div>
                  )
                ))}
                {(g.members || []).length > 5 && (
                  <div className="w-7 h-7 rounded-full border-2 border-[#181824] bg-[#3F3F46] flex items-center justify-center text-white text-[8px]">+{g.members.length - 5}</div>
                )}
              </div>
            </motion.div>
          ))
        )}
      </div>

      <BottomNav />
    </div>
  );
}
